/**
 * Run PMW main loader
 */

const main = async () => {

	// The event bus is the first thing to load: everything else registers
	// listeners or emits through it.
	require("./pmw/bus");

	require("./pmw/functions_main");
	require("./version.js");
	require("./platform/wp-common/wp-environment");

	// Wait until jQuery is loaded
	await pmw.jQueryExists();

	// jQuery bridge: keeps documented legacy jQuery snippets working in both
	// directions. Loaded after the jQuery wait and before any listener
	// registrations (its inbound forwarding hooks pmw.bus.on).
	require("./platform/wp-common/jquery-bridge");
	require("./platform/wp-common/wp-ajax");

	// Platform layer: WooCommerce shop knowledge (product/cart detection,
	// pmw.platform surface). Loaded before the listener files that use it.
	require("./platform/woocommerce/adapter");
	require("./platform/woocommerce/product-detection");

	// WooCommerce Blocks / Interactivity API bridge. Must load before the
	// listener files: they register their bindings through pmw.bindAndRebind.
	require("./platform/woocommerce/interactivity-dom");

	// Wait until pmwDataLayer is loaded
	await pmw.dataLayerFullyLoaded();

	/**
	 * Set webpack public path for chunk loading
	 * This ensures chunks load correctly even when scripts are combined/moved 
	 * by optimization plugins (SiteGround, Autoptimize, WP Rocket, etc.)
	 */
	if (pmwDataLayer?.general?.chunk_base_path) {
		// eslint-disable-next-line no-undef, camelcase
		__webpack_public_path__ = pmwDataLayer.general.chunk_base_path;
	}

	// wait 1000ms
	// await new Promise(resolve => setTimeout(resolve, 1000))

	// Load all essential scripts first
	require("./pmw/functions_loader");

	// Initialize cross-tab session sync early so that session data
	// from existing tabs is available before event listeners run
	if (pmw.crossTabSync) {
		pmw.crossTabSync.init();
		window.addEventListener("beforeunload", () => pmw.crossTabSync.cleanup());
	}
	
	// Load new event filtering system
	require("./pmw/hooks");
	require("./pmw/pixel-registry");
	require("./pmw/event-processor");

	// Output PMW console message
	pmw.pmwConsoleMessage();

	// Load the consent management module as a chunk.
	//
	// Wrapped in try/catch so a failed dynamic import (e.g. the consent chunk
	// 404s because the entry script and chunk base path were built from
	// different tier directories, or a CDN/cache plugin serves a stale HTML
	// referencing chunks from a previous build) does not abort the rest of
	// main(). On failure we fall back to a deny-by-default consent state so no
	// pixels fire without consent — a tracking gap is far better than firing
	// without consent.
	//
	// @since 1.58.10
	try {
		await pmw.loadConsentModule();
		await pmw.consent.load();
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error("[PMW] Consent module failed to load — falling back to deny-by-default consent state. Tracking will be disabled until the issue is resolved.", error);

		// Provide a minimal pmw.consent surface that mirrors the real module
		// shape just enough that downstream code (pixels, listeners) doesn't
		// throw on access. All categories are denied. Necessary stays true so
		// non-tracking functionality continues to work.
		const denied = {
			categories: {
				statistics : false,
				marketing  : false,
				preferences: false,
				necessary  : true,
			},
			visitorHasChosen: false,
		};
		pmw.consent = {
			// Marks the fallback surface. Pixel loaders that would otherwise
			// bypass the category checks (Google Consent Mode keeps loading
			// gtag.js on denied consent by design) must check this flag and
			// stay off: a broken consent pipeline cannot deliver reliable
			// consent signals, so nothing may be loaded at all.
			isFallback: true,
			settings  : {
				get: () => denied,
				set: () => {},
			},
			categories: {
				get: () => denied.categories,
				set: () => {},
			},
			load                     : async () => {},
			explicitConsentModeActive: () => false,
			canPixelBeFired          : () => false,
			logSuppressedPixel       : () => {},
			cmpConsentClickObserver  : () => {},
			api                      : {
				acceptAll         : () => {},
				revokeAll         : () => {},
				updateSelectively : () => {},
				getConsentStateFor: () => false,

				// The gtag wrapper calls this on every gtag() invocation. It must
				// exist here, or the Google Consent Mode bootstrap throws before
				// even the denied consent default has been queued.
				processExternalGcmConsentUpdate_experimental: () => {},
			},
		};
	}

	// Run the command queue.
	//
	// This must happen after the consent module is available and before any
	// event listener is registered or any event fires:
	//
	// - After consent: queued snippets legitimately read pmw.consent at their
	//   top level. Our own Consent API documentation tells integrators to call
	//   pmw.consent.api.acceptAll() from inside _pmwq. While the queue ran
	//   before loadConsentModule(), pmw.consent was undefined at that point, the
	//   snippet threw, the error was swallowed by the try/catch in
	//   initializeCommandQueue() and everything after the failing line -
	//   typically the integration's event listener registration - was silently
	//   skipped.
	// - Before event listeners: queued snippets register event filters and
	//   pmw:* listeners, which must be in place before the first event fires.
	//
	// The window between the two is exactly this function, so keep the call
	// here when adding new initialization steps.
	//
	// @since 1.65.0
	pmw.initializeCommandQueue();

	// Doesn't work if we listen to pmw:dom:ready because it runs too late
	// (at the end of the main.js file)
	// TODO: Find out why that order is important and optimize it. It is also difficult to test. In Cypress it works just with "pmw:dom:ready"
	jQuery(() => {
		require("./platform/woocommerce/event_listeners_on_ready");
	});

	// Load remaining event listeners
	require("./platform/woocommerce/event_listeners");

	// DOM level fallbacks for the blocks funnel, deduplicated against the
	// wp.hooks path loaded below.
	require("./platform/woocommerce/blocks-fallbacks");

	// Load doofinder listener as a chunk
	import(/* webpackChunkName: "doofinder-listener" */ "./platform/woocommerce/listeners/doofinder");

	pmw.loadWcHooksFunctions();

	// Check if domain is excluded from tracking
	// If so, return
	if (pmw.excludeDomainFromTracking()) return;

	// Initialize IP exclusion flag (defaults to false)
	// Resolve the visitor-allowed decision right here for every case that needs
	// no IP lookup (cached decision, bot user-agent, automated browser, no
	// exclusion list), so pmw._ipExcluded is set before pixels fire their first
	// events. isVisitorAllowedSync() sets the flag itself and returns null only
	// when an exclusion list is configured and the decision isn't cached yet, in
	// which case the async check is kicked off to do the IP lookup.
	pmw._ipExcluded = false;
	if (pmw.isVisitorAllowedSync() === null) {
		pmw.isVisitorAllowed(); // fire-and-forget; sets pmw._ipExcluded when resolved
	}

	// Load pixels dynamically based on configuration
	const pixelLoaders = [];

	// Load free pixels conditionally
	if (pmwDataLayer?.pixels?.google) {
		pixelLoaders.push(
			import(/* webpackChunkName: "pixel-google" */ "./pixels/google/loader")
		);
	}
	
	if (pmwDataLayer?.pixels?.facebook) {
		pixelLoaders.push(
			import(/* webpackChunkName: "pixel-facebook" */ "./pixels/facebook/loader")
		);
	}
	
	if (pmwDataLayer?.pixels?.hotjar) {
		pixelLoaders.push(
			import(/* webpackChunkName: "pixel-hotjar" */ "./pixels/hotjar/loader")
		);
	}

	// #if process.env.TIER === 'premium'
	/**
	 *  Load premium pixels conditionally
	 */
	if (pmw.canLoadPremiumFeatures()) {
		pixelLoaders.push(
			import(/* webpackChunkName: "pmw-premium-events" */ "./platform/woocommerce/event_listeners_premium")
		);

		if (pmwDataLayer?.pixels?.crazyegg) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-crazyegg" */ "./pixels/crazyegg/loader")
			);
		}

		if (pmwDataLayer?.pixels?.contentsquare) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-contentsquare" */ "./pixels/contentsquare/loader")
			);
		}

		if (pmwDataLayer?.pixels?.clarity) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-clarity" */ "./pixels/clarity/loader")
			);
		}

		if (pmwDataLayer?.pixels?.triple_whale) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-triple-whale" */ "./pixels/triple-whale/loader")
			);
		}

		if (pmwDataLayer?.pixels?.hyros) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-hyros" */ "./pixels/hyros/loader")
			);
		}

		if (pmwDataLayer?.pixels?.mixpanel) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-mixpanel" */ "./pixels/mixpanel/loader")
			);
		}

		if (pmwDataLayer?.pixels?.groundtruth) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-groundtruth" */ "./pixels/groundtruth/loader")
			);
		}

		if (pmwDataLayer?.pixels?.criteo) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-criteo" */ "./pixels/criteo/loader")
			);
		}

		if (pmwDataLayer?.pixels?.nextdoor) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-nextdoor" */ "./pixels/nextdoor/loader")
			);
		}

		if (pmwDataLayer?.pixels?.adroll) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-adroll" */ "./pixels/adroll/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.linkedin) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-linkedin" */ "./pixels/linkedin/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.bing) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-microsoft" */ "./pixels/microsoft-ads/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.outbrain) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-outbrain" */ "./pixels/outbrain/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.pinterest) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-pinterest" */ "./pixels/pinterest/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.snapchat) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-snapchat" */ "./pixels/snapchat/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.taboola) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-taboola" */ "./pixels/taboola/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.tiktok) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-tiktok" */ "./pixels/tiktok/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.twitter) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-twitter" */ "./pixels/twitter/loader")
			);
		}
		
		if (pmwDataLayer?.pixels?.reddit) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-reddit" */ "./pixels/reddit/loader")
			);
		}

		if (pmwDataLayer?.pixels?.openai) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-openai" */ "./pixels/openai/loader")
			);
		}

		if (pmwDataLayer?.pixels?.vwo) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-vwo" */ "./pixels/vwo/loader")
			);
		}

		if (pmwDataLayer?.pixels?.optimizely) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-optimizely" */ "./pixels/optimizely/loader")
			);
		}

		if (pmwDataLayer?.pixels?.ab_tasty) {
			pixelLoaders.push(
				import(/* webpackChunkName: "pixel-ab-tasty" */ "./pixels/ab_tasty/loader")
			);
		}
	}
	// #endif

	// Load all active pixels in parallel
	await Promise.all(pixelLoaders);

	/** Load the deprecated functions as a chunk (if enabled) */
	if (pmwDataLayer?.general?.modules?.load_deprecated_functions !== false) {
		const {loadDeprecatedFunctions} = await import(/* webpackChunkName: "deprecated-functions" */ "./pmw/deprecated.mjs");
		loadDeprecatedFunctions();
	}

	// Preload pixels. Emitted on the bus (library listeners) and kept as a
	// native DOM event for third-party addEventListener snippets.
	pmw.bus.emit("pmw:load-pixels");
	document.dispatchEvent(new Event("pmw:load-pixels"));

	// Trigger pmwLoad event
	pmw.bus.emit("pmwLoad");
	document.dispatchEvent(new Event("pmwLoad"));

	// Deliver the entry events that were dispatched while the library was still
	// starting up, by a third-party snippet or by our own early listeners.
	// Everything they need exists now: the listeners are registered, the pixels
	// are loaded and initialized, and the pmwLoad handlers have set up the cart
	// and the click IDs. Before this point they would have been processed by
	// nobody, which is why the bus held them.
	//
	// @since 1.65.2
	pmw.bus.flushEntryEvents();

	// First-run onboarding: one-shot "pixels are live" confirmation. The
	// server injects the flag only on fresh installs until the first
	// confirmation is stored, so this chunk is never requested otherwise.
	if (pmwDataLayer?.general?.first_event_confirmation) {
		import(/* webpackChunkName: "first-event-confirmation" */ "./pmw/first-event-confirmation.mjs")
			.then(({initFirstEventConfirmation}) => initFirstEventConfirmation())
			.catch(() => {});
	}

	// Wait until page is loaded
	await pmw.pageLoaded();

	pmw.bus.emit("pmw:ready");
	document.dispatchEvent(new Event("pmw:ready"));
	pmw.triggerPmwDomReadyEvent();

	// Check if the library version matches the PMW version that's installed on the server
	pmw.checkLibraryVersion();
};

main();








